import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from '../../@commons/ui-components/shared.module';
import { Plant } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/plant.entity';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { ActivatedRoute } from '@angular/router';
import {
  PlantResponse
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/plant.response';
import { NeedsComponent } from '../@components/needs/needs.component';
import { StorageService } from '../../@commons/@services/front/storage.service';
import { HexToHsl } from '../../@commons/pipes/HexToHsl';
import { CalendarPeriodLineComponent } from '../../calendar/@components/calendar-hierarchies/calendar-hierarchy-card/calendar-hierarchy-card-item/calendar-period-line/calendar-period-line.component';
import { CultivationPeriodListRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/cultivation-period-list.request';
import { CultivationPeriodApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-period/cultivation-period-api';
import { CalendarPeriodLegendComponent } from '../../calendar/@components/calendar-period-legend/calendar-period-legend.component';
import { PERIOD_KINDS, PeriodKind, ShelterablePeriod, periodKind } from '../../@commons/@services/front/period-bars.util';
import { CultivationRelationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-relation/cultivation-relation-api';
import { CloseIconComponent } from '../../@icons/close-icon.component';
import { CheckIconComponent } from '../../@icons/check-icon.component';
import { DEFAULT_CULTIVATION_IMAGE } from '../@components/last-file-url.util';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import { ArticleListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/article/article-list.response';
import { ShopRedirectService } from '../../@commons/@services/front/shop-redirect.service';
import { CultivationRelationType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/cultivation-relation-type.enum';
import { cultivationFamilyName } from '../@components/cultivation-family.util';

@Component({
  selector: 'vw-cultivation-guide',
  imports: [
    SharedModule,
    CommonModule,
    TranslatePipe,
    NeedsComponent,
    HexToHsl,
    CalendarPeriodLineComponent,
    CalendarPeriodLegendComponent,
    CloseIconComponent,
    CheckIconComponent,
  ],
  providers: [
    PlantApi,
    CultivationPeriodApi,
    StorageService,
    CultivationRelationApi,
    ArticleApi
  ],
  templateUrl: './cultivation-guide.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./cultivation-guide.component.scss']
})
export class CultivationGuideComponent {
  risks: any[] = []; // Holds the risks associated with the plant
  relations: { name: string, type?: CultivationRelationType }[] = []; // Holds the companion plants for the plant
  articles: ArticleListResponse[] = [];
  @Input() plant: Plant | null = null;
  @Input() cultivationVariant: any;
  @Input() cultivation: any;
  /** Dans une feuille : l'en-tête laisse la place à la croix posée en haut à droite. */
  @Input() inSheet = false;

  /** Cultures qui s'entendent bien avec celle-ci. */
  get friends() {
    return this.relations.filter((relation) => relation.type === CultivationRelationType.FRIENDS);
  }

  /** Cultures à éloigner. */
  get enemies() {
    return this.relations.filter((relation) => relation.type === CultivationRelationType.ENEMIES);
  }

  /** Photo de la culture (la première, comme avant), sinon l'image par défaut. */
  get photoUrl(): string {
    return this.cultivation?.files?.[0]?.cdnUrl || DEFAULT_CULTIVATION_IMAGE;
  }


  /** Périodes de la variété : à l'abri, en place, plantation, récolte (PC-11). */
  periods: ShelterablePeriod[] = [];
  /** Natures présentes, pour une légende sans mot inutile. */
  periodKinds: PeriodKind[] = [];

  constructor(private plantApi: PlantApi,
    private cultivationPeriodApi: CultivationPeriodApi,
    private cultivationRelationApi: CultivationRelationApi,
    private articleApi: ArticleApi,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private shopRedirect: ShopRedirectService) { }

  /** Opens the webshop (external browser) to buy seeds of this cultivation. */
  onBuySeeds(): void {
    const cultivationUid = this.cultivation?.uid ?? this.cultivationVariant?.cultivation?.uid;
    this.shopRedirect.buyForCultivation(cultivationUid);
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * Fetches plant details from the service.
   */
  async ngOnInit() {
    const plantUid = this.route.snapshot.paramMap.get('plantUid'); // Get the plant ID from route parameters
    await this.loadPlant(plantUid);
    //this.risks =  plant?.cultivationVariant?.risks//TODO
    //this.friends = plant?.cultivationVariant?.friends//TODO
    await this.loadPeriods();
    await this.loadAssociations();
  }

  async loadPlant(plantUid: string | null) {
    if (plantUid) {
      this.plantApi.find(plantUid, this.storageService.getCurrentLanguage()).then((res: PlantResponse) => {
        this.plant = res; // Assign the fetched plant details to the component property
      });
    }
  }

  /**
   * Method to load the cultivation periods for the variant.
   */
  async loadPeriods() {
    if (this.cultivationVariant) {
      const listCultivationPeriods: CultivationPeriodListRequest = new CultivationPeriodListRequest();
      listCultivationPeriods.cultivationVariantUid = this.cultivationVariant.uid;
      const periods = await this.cultivationPeriodApi.list(listCultivationPeriods, 0, 20, this.storageService.getCurrentLanguage());
      this.periods = periods.results;
      const kinds = new Set(this.periods.map((period) => periodKind(period)));
      this.periodKinds = PERIOD_KINDS.filter((kind) => kinds.has(kind));
    }
  }

  async loadAssociations() {
    if (this.cultivation) {
      const relations = await this.cultivationRelationApi.list({ cultivationUid: this.cultivation.uid }, 0, 20, this.storageService.getCurrentLanguage());
      this.relations = relations.results.map(relation => ({
        name: relation.cultivation1.uid === this.cultivation.uid ? relation.cultivation2.name.toString() : relation.cultivation1.name.toString(),
        type: relation.relationType
      })).sort((a, b) => a.type === 'FRIENDS' ? -1 : 1);
    }
  }

  /** Le nom de la famille (« Solanacées »), jamais le nom latin ni le genre. */
  getFamily(): string {
    return cultivationFamilyName(this.cultivation?.cultivationHierarchy, this.cultivation?.agronomy?.family);
  }
}
